<script setup lang="ts">
    import DataTableActions from '@/components/datatable/DataTableActions.vue';
    import { Voucher, SharedData } from '@/types';
    import { usePermissions } from '@/composables/usePermissions';
    import Permission from '@/enums/Permission';
    import { Button } from '@/components/ui/button';
    import Delete from '@/components/Delete.vue';
    import QueryKey from '@/enums/QueryKey';
    import { usePage } from '@inertiajs/vue3';
    import { computed } from 'vue';
    import DownloadVouchers from './DownloadVouchers.vue';
    import DownloadStatements from './DownloadStatements.vue';
    import EditVoucher from './EditVoucher.vue';
    import ApproveVoucher from './ApproveVoucher.vue';

    interface Props {
        voucher: Voucher;
    }

    defineProps<Props>();

    const { hasPermission } = usePermissions();

    const page = usePage<SharedData>();
    const selectedCompanyId = computed(() => page.props.auth.user.selected_company_id);
</script>

<template>
    <DataTableActions :data="voucher">
        <div
            v-if="!voucher.uploaded"
            class="flex gap-1"
        >
            <div v-if="hasPermission(Permission.VOUCHERS_UPDATE) || hasPermission(Permission.VOUCHERS_OWN_UPDATE)">
                <div
                    v-if="voucher.has_voucher || voucher.is_valid_no_file"
                    class="flex gap-1"
                >
                    <EditVoucher
                        :voucher="voucher"
                        button-text="forms.edit"
                        title="forms.edit"
                    />
                    <ApproveVoucher v-if="hasPermission(Permission.VOUCHERS_UPDATE)" :voucher="voucher" />
                    <DownloadVouchers v-else :voucher="voucher" />
                </div>

                <EditVoucher
                    v-else
                    :voucher="voucher"
                    button-text="vouchers.missing_txt"
                    title="vouchers.missing_txt"
                />
            </div>
        </div>
        <div
            v-else
            class="flex gap-1"
        >
            <div v-if="hasPermission(Permission.VOUCHERS_VIEW) || hasPermission(Permission.VOUCHERS_OWN_VIEW)">
                <div
                    v-if="voucher.has_voucher"
                    class="flex gap-1"
                >
                    <DownloadVouchers :voucher="voucher" />
                    
                    <Delete
                    v-if="hasPermission(Permission.VOUCHERS_ADMIN_DELETE)"
                        :route="route('v2.voucher.admin.attachment.delete', voucher.id)"
                        :query-key="[QueryKey.VOUCHERS, selectedCompanyId]"
                        :button-text="$t('vouchers.delete')"
                        header="vouchers.select.title"
                        description="vouchers.select.delete_voucher_confirm"
                        :continue-button-text="$t('vouchers.delete')"
                    />
                </div>
                <DownloadStatements
                    v-else-if="voucher.has_statement"
                    :statement="voucher.statement"
                />
                <Button
                    v-else-if="voucher.is_valid_no_file"
                    variant="delete"
                    size="xs"
                    :disabled="true"
                >
                    {{ $t('button-text.uploaded-without-voucher') }}
                </Button>
            </div>
        </div>
    </DataTableActions>
</template>
